import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { householdsApi, formatError } from "../lib/api";
import {
  HOUSEHOLD_ICON_OPTIONS,
  getHouseholdIcon,
} from "../lib/householdIcons";

/**
 * HouseholdIconPicker — compact inline picker that sits next to the
 * household name. Renders the current icon as a button with a tiny pencil
 * badge; tapping it opens a small popup with the 7 options (icon-only,
 * no labels).
 *
 * Owners can change the icon. Non-owners see the icon as a read-only chip
 * with no pencil and no popup.
 */
export const HouseholdIconPicker = ({ household, currentUserId, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  if (!household) return null;

  const isOwner = household.members?.some(
    (m) => m.id === currentUserId && m.is_owner,
  );
  const currentSlug = household.icon || "home";
  const CurrentIcon = getHouseholdIcon(currentSlug);

  const pick = async (slug) => {
    if (slug === currentSlug || busy) {
      setOpen(false);
      return;
    }
    setBusy(slug);
    try {
      const updated = await householdsApi.setIcon(slug);
      toast.success("Household icon updated");
      onChanged?.(updated);
      setOpen(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => isOwner && setOpen(true)}
        disabled={!isOwner}
        data-testid="household-icon-button"
        aria-label={isOwner ? "Change household icon" : "Household icon"}
        className={`relative shrink-0 w-12 h-12 rounded-2xl brand-gradient-bg flex items-center justify-center text-white shadow-sm ${
          isOwner
            ? "hover:shadow-md transition-shadow cursor-pointer"
            : "cursor-default"
        }`}
      >
        <CurrentIcon className="w-6 h-6" />
        {isOwner && (
          <span
            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border border-[#E2E0D8] flex items-center justify-center shadow-sm"
            aria-hidden
          >
            <Pencil className="w-2.5 h-2.5 text-[#4F5D75]" />
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !busy && setOpen(false)}
            className="fixed inset-0 z-[2147483600] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center"
            data-testid="household-icon-sheet-overlay"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[360px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-5 sm:m-4"
              data-testid="household-icon-sheet"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="font-heading text-base font-semibold text-[#2D3142]">
                  Household icon
                </div>
                <button
                  type="button"
                  onClick={() => !busy && setOpen(false)}
                  data-testid="household-icon-sheet-close"
                  aria-label="Close"
                  className="w-8 h-8 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div
                className="grid grid-cols-4 gap-3"
                data-testid="household-icon-options"
              >
                {HOUSEHOLD_ICON_OPTIONS.map(({ slug, label, Icon }) => {
                  const selected = slug === currentSlug;
                  const loading = busy === slug;
                  return (
                    <button
                      key={slug}
                      type="button"
                      onClick={() => pick(slug)}
                      disabled={!!busy}
                      data-testid={`household-icon-option-${slug}`}
                      aria-label={label}
                      aria-pressed={selected}
                      className={`relative aspect-square rounded-2xl flex items-center justify-center border-2 transition-all ${
                        selected
                          ? "border-[#277A82] bg-[#DCEDF0]/60 shadow-sm"
                          : "border-[#E2E0D8] bg-white hover:border-[#277A82]/40 hover:bg-[#F5F3ED]"
                      } ${loading ? "opacity-70" : ""}`}
                    >
                      {selected && (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#277A82] text-white flex items-center justify-center shadow"
                          data-testid={`household-icon-selected-${slug}`}
                        >
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </motion.span>
                      )}
                      {loading ? (
                        <Loader2 className="w-5 h-5 text-[#277A82] animate-spin" />
                      ) : (
                        <Icon
                          className={`w-6 h-6 ${
                            selected ? "text-[#1F5566]" : "text-[#4F5D75]"
                          }`}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HouseholdIconPicker;
